import * as React from 'react';

import { GenerationStatus } from '@showbie-socrative/socrative-utils';
import { getActivityInsights } from '~api/activities/insights/getActivityInsights';
import type { ActivityInsights } from '~api/activities/insights/insightSchema';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import type { Poll, PollConfig, PollResult } from '~shared/polling';
import { PollStatus } from '~shared/polling';
import { queryClient } from '~teacher/queryClient';

import { queryKey } from '../hook/activity/queryKey';

interface InsightsMetadata {
  activityInstanceId: number;
}

const showSuccessToast = (activityInstanceId: number) => {
  const message = (toastInstance: any) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      toast.dismiss(toastInstance.id);
      window.location.hash = `#results/${activityInstanceId}/showInsights=true`;
    };

    return (
      <div className="flex items-center gap-2">
        <span>{translate('Your AI Insights are ready.')}</span>
        <a
          href={`#results/${activityInstanceId}/showInsights=true`}
          onClick={handleClick}
          className="text-sm text-white leading-6 font-medium underline hover:no-underline cursor-pointer"
        >
          {translate('View Now')}
        </a>
      </div>
    );
  };

  toast.positive(message, {
    duration: 5000,
    disableClose: false,
  });
};

export const insightsPollerConfig: PollConfig<
  ActivityInsights,
  InsightsMetadata
> = {
  type: 'ai-insights',

  pollFn: async (
    poll: Poll<ActivityInsights, InsightsMetadata>
  ): Promise<PollResult<ActivityInsights>> => {
    const { activityInstanceId } = poll.metadata;

    const activityInsights = await getActivityInsights(activityInstanceId);

    if (!activityInsights) {
      return {
        status: PollStatus.Pending,
        shouldContinuePolling: true,
      };
    }

    const status = activityInsights.generationStatus;

    if (status === GenerationStatus.Completed) {
      return {
        status: PollStatus.Completed,
        data: activityInsights,
        shouldContinuePolling: false,
      };
    } else if (status === GenerationStatus.Failed) {
      return {
        status: PollStatus.Failed,
        data: activityInsights,
        shouldContinuePolling: false,
        error: 'Insights generation failed',
      };
    } else {
      return {
        status: PollStatus.Pending,
        data: activityInsights,
        shouldContinuePolling: true,
      };
    }
  },

  onComplete: (poll, result) => {
    console.debug(
      '[InsightsPoller] Polling completed, invalidating cache',
      poll,
      result
    );
    const { activityInstanceId } = poll.metadata as unknown as InsightsMetadata;

    // Invalidate React Query cache to trigger UI update
    queryClient.invalidateQueries(queryKey.insightsById(activityInstanceId));

    // Show success toast
    showSuccessToast(activityInstanceId);
  },

  onFailure: (poll, error) => {
    console.debug('[InsightsPoller] Polling failed', poll, error);
    toast.critical(translate('Unexpected Error'));
  },

  onTimeout: (poll) => {
    console.debug('[InsightsPoller] Polling timed out', poll);
    toast.critical(translate('Unexpected Error'));
  },

  pollingStrategy: {
    initialInterval: 5000, // Start polling after 5 seconds for AI insights
    maxInterval: 30000,
    maxDuration: 5 * 60 * 1000,
    useExponentialBackoff: true,
  },
};
